import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useGetBillingStatus } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { CreditCard, Check, Star, Zap, Shield, Copy, Share2, Users, Clock, CheckCircle2, XCircle, Gift, ExternalLink, AlertCircle, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ReferralData {
  referralCode: string | null;
  referrals: Array<{
    id: string;
    referredGymName: string;
    status: "PENDING" | "CONVERTED" | "EXPIRED";
    createdAt: string;
    creditIssuedAt: string | null;
  }>;
}

function ReferralProgramSection() {
  const { toast } = useToast();
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchReferralData = useCallback(async () => {
    try {
      const data = await apiFetch<ReferralData>("/referral/my");
      setReferralData(data);
    } catch {
      setReferralData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const shareableLink = referralData?.referralCode
    ? `${window.location.origin}/register?ref=${referralData.referralCode}`
    : null;

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: "Copied!", description: `${label} copied to clipboard.` });
    });
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case "CONVERTED":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
            <CheckCircle2 className="w-3 h-3" /> Converted
          </span>
        );
      case "EXPIRED":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-400">
            <XCircle className="w-3 h-3" /> Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
    }
  }

  if (loading) {
    return (
      <Card className="glass-panel border-white/10">
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading referral data...
        </CardContent>
      </Card>
    );
  }

  if (!referralData) {
    return null;
  }

  return (
    <Card className="glass-panel border-white/10">
      <CardHeader>
        <CardTitle className="text-2xl font-display text-white flex items-center gap-2">
          <Gift className="w-6 h-6 text-primary" /> Referral Program
        </CardTitle>
        <CardDescription>
          Refer other gyms and earn a free month of billing when they subscribe
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {referralData.referralCode ? (
          <>
            <div className="space-y-3">
              <label className="text-sm font-medium text-muted-foreground">Your Referral Code</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-background border border-white/10 rounded-xl px-4 py-3 font-mono text-lg text-white tracking-wider">
                  {referralData.referralCode}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 border-white/10 hover:bg-white/5"
                  onClick={() => copyToClipboard(referralData.referralCode!, "Referral code")}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {shareableLink && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-muted-foreground">Shareable Link</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-background border border-white/10 rounded-xl px-4 py-3 text-sm text-muted-foreground truncate">
                    {shareableLink}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-12 w-12 border-white/10 hover:bg-white/5"
                    onClick={() => copyToClipboard(shareableLink, "Shareable link")}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No referral code has been generated for your gym yet.
          </p>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Users className="w-4 h-4" /> Your Referrals
            </h3>
            {referralData.referrals.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {referralData.referrals.filter(r => r.status === "CONVERTED").length} converted
              </span>
            )}
          </div>

          {referralData.referrals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-white/10 rounded-xl">
              No referrals yet. Share your code to get started!
            </div>
          ) : (
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Gym</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Date</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {referralData.referrals.map((referral) => (
                    <tr key={referral.id} className="border-b border-white/5 last:border-0">
                      <td className="px-4 py-3 text-sm text-white">{referral.referredGymName}</td>
                      <td className="px-4 py-3">{getStatusBadge(referral.status)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(referral.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        {referral.creditIssuedAt ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-400">
                            <CheckCircle2 className="w-3 h-3" /> Applied
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface StripeConnectStatus {
  connected: boolean;
  onboarded: boolean;
  payoutsEnabled: boolean;
  accountId: string | null;
}

function StripeConnectSection({ gymId }: { gymId: string }) {
  const { toast } = useToast();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiFetch<StripeConnectStatus>(`/billing/gym/${gymId}/stripe-connect-status`);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get("stripe");
    if (!stripeParam) return;

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);

    if (stripeParam === "complete") {
      apiFetch<{ onboarded: boolean }>(`/billing/gym/${gymId}/stripe-connect/complete`, { method: "POST" })
        .then((result) => {
          if (result.onboarded) {
            toast({ title: "Stripe Connected", description: "Your Stripe account has been successfully connected." });
          } else {
            toast({ title: "Almost Done", description: "Please complete the remaining Stripe onboarding steps.", variant: "destructive" });
          }
          fetchStatus();
        })
        .catch(() => {
          toast({ title: "Error", description: "Failed to verify Stripe connection status.", variant: "destructive" });
        });
    } else if (stripeParam === "refresh") {
      toast({ title: "Session Expired", description: "Your Stripe onboarding session expired. Please try again." });
    }
  }, [gymId, toast, fetchStatus]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const result = await apiFetch<{ url?: string }>(`/billing/gym/${gymId}/stripe-connect`, { method: "POST" });
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast({ title: "Error", description: "No onboarding URL returned", variant: "destructive" });
        setConnecting(false);
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to start Stripe onboarding", variant: "destructive" });
      setConnecting(false);
    }
  }

  if (loading) {
    return (
      <Card className="glass-panel border-white/10">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
          Checking Stripe connection...
        </CardContent>
      </Card>
    );
  }

  const isFullyConnected = status?.connected && status?.onboarded;
  const isPartiallyConnected = status?.connected && !status?.onboarded;

  return (
    <Card className={cn("border-2 transition-colors", isFullyConnected ? "border-green-500/30 glass-panel" : "border-primary/30 glass-panel")}>
      <CardHeader>
        <CardTitle className="text-2xl font-display text-white flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-primary" /> Stripe Connect
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to accept payments from students
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isFullyConnected && (
          <div className="rounded-xl border border-green-500/20 bg-green-500/10 px-4 py-4 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-sm font-semibold text-green-400">Stripe Account Connected</span>
            </div>
            <p className="text-xs text-green-300/70">
              Your gym is set up to accept payments.
              {status?.payoutsEnabled ? " Payouts are enabled." : " Payouts are pending verification by Stripe."}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-white/10 text-muted-foreground hover:bg-white/5"
              onClick={handleConnect}
              disabled={connecting}
            >
              <ExternalLink className="w-3 h-3 mr-2" />
              {connecting ? "Opening..." : "Update Stripe Settings"}
            </Button>
          </div>
        )}

        {isPartiallyConnected && (
          <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-4 space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-semibold text-yellow-400">Onboarding Incomplete</span>
            </div>
            <p className="text-xs text-yellow-300/70">
              Your Stripe account has been created but onboarding isn't finished yet. Please complete the setup to start accepting payments.
            </p>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-primary text-primary-foreground hover:bg-primary/80"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {connecting ? "Opening..." : "Continue Setup"}
            </Button>
          </div>
        )}

        {!status?.connected && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Connect your Stripe account so your gym can accept session payments from students. Stripe handles all payment processing securely.
            </p>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-primary text-primary-foreground hover:bg-primary/80"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {connecting ? "Setting up..." : "Connect Stripe Account"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminBilling() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: billing, isLoading, refetch } = useGetBillingStatus(user?.gymId || "", { query: { enabled: !!user?.gymId, retry: false } });
  const [upgrading, setUpgrading] = useState(false);

  const isPremium = billing?.platformPlan === "PREMIUM";
  const isTrialing = billing?.isTrialing === true;
  const [cancelling, setCancelling] = useState(false);

  async function handleUpgrade() {
    if (!user?.gymId) return;
    setUpgrading(true);
    try {
      const freshBilling = await refetch();
      if (freshBilling.data?.platformPlan === "PREMIUM") {
        toast({ title: "Already Premium", description: "Your gym is already on the Premium plan." });
        setUpgrading(false);
        return;
      }

      const result = await apiFetch<{ url?: string }>(`/billing/gym/${user.gymId}/upgrade`, { method: "POST" });
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast({ title: "Error", description: "No checkout URL returned", variant: "destructive" });
        setUpgrading(false);
      }
    } catch (e: unknown) {
      const err = e instanceof Error ? e : new Error("Unknown error");
      const isAlreadyPremium =
        typeof e === "object" && e !== null &&
        "status" in e && (e as { status: unknown }).status === 400 &&
        err.message.includes("already on the Premium plan");
      if (isAlreadyPremium) {
        await refetch();
        toast({ title: "Already Premium", description: "Your gym is already on the Premium plan." });
      } else {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
      setUpgrading(false);
    }
  }

  async function handleCancelTrial() {
    if (!user?.gymId) return;
    setCancelling(true);
    try {
      await apiFetch(`/billing/gym/${user.gymId}/downgrade`, { method: "POST" });
      toast({ title: "Trial Cancelled", description: "Your free trial has been cancelled. You were not charged." });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  }

  const features = {
    free: ["Up to 1 location", "$3 per session fee", "Core booking features", "Basic analytics"],
    premium: ["Unlimited locations", "$1 per session fee", "White-label branding", "Advanced analytics", "Priority support", "Custom domain"],
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-4xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Billing & Plan</h1>
          <p className="text-muted-foreground">Manage your subscription</p>
        </div>

        {user?.gymId && <StripeConnectSection gymId={user.gymId} />}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className={cn("border-2 transition-colors", !isPremium ? "border-primary glass-panel" : "border-white/10 glass-panel")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-display text-white">Free</CardTitle>
                {!isPremium && <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full font-semibold">Current Plan</span>}
              </div>
              <CardDescription className="text-3xl font-mono text-white mt-2">$0<span className="text-sm text-muted-foreground">/month</span></CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {features.free.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-green-400 shrink-0" /> {f}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className={cn("border-2 transition-colors", isPremium ? "border-primary glass-panel" : "border-white/10 glass-panel")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-display text-white flex items-center gap-2"><Star className="w-5 h-5 text-primary" /> Premium</CardTitle>
                {isPremium && !isTrialing && <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full font-semibold">Current Plan</span>}
                {isPremium && isTrialing && <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full font-semibold">Free Trial</span>}
              </div>
              <CardDescription className="text-3xl font-mono text-white mt-2">$49<span className="text-sm text-muted-foreground">/month</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isTrialing && billing?.trialEndsAt && (
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 space-y-1">
                  <p className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Free Trial Active
                  </p>
                  <p className="text-xs text-emerald-300/70">
                    Your free trial ends on {new Date(billing.trialEndsAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}. You won't be charged until then.
                  </p>
                </div>
              )}
              <ul className="space-y-3">
                {features.premium.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-primary shrink-0" /> {f}</li>
                ))}
              </ul>
              {!isPremium && (
                <Button onClick={handleUpgrade} disabled={upgrading} className="w-full bg-primary text-primary-foreground hover:bg-primary/80">
                  <Zap className="w-4 h-4 mr-2" /> {upgrading ? "Processing..." : "Start Free Trial"}
                </Button>
              )}
              {isPremium && isTrialing && (
                <Button onClick={handleCancelTrial} disabled={cancelling} variant="outline" className="w-full border-white/10 text-muted-foreground hover:bg-white/5">
                  {cancelling ? "Cancelling..." : "Cancel Trial"}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <ReferralProgramSection />
      </div>
    </AppLayout>
  );
}
